import React, { useEffect, useState } from "react";
import axios from "axios";
import { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import VehiclePicker from "@/components/VehiclePicker";
import { useVocab } from "@/lib/vocab";

const HERO = "https://images.pexels.com/photos/27692895/pexels-photo-27692895.jpeg";

export default function Booking() {
  const voc = useVocab();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotDate, setSlotDate] = useState("");
  const [slotLoading, setSlotLoading] = useState(false);

  const loadSlots = async (dateStr) => {
    setSlotDate(dateStr); setForm((f) => ({ ...f, preferred_date: "" }));
    if (!dateStr) { setSlots([]); return; }
    setSlotLoading(true);
    try {
      const r = await axios.get(`${API}/public/availability`, { params: { date: dateStr } });
      setSlots(r.data.slots || []);
    } catch { setSlots([]); } finally { setSlotLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/public/booking-config`).then((r) => {
      setConfig(r.data);
      if (r.data.accent_color) document.documentElement.style.setProperty("--accent", r.data.accent_color);
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fields = (config?.booking_fields || []).filter((f) => f.enabled);
  const has = (k) => fields.some((f) => f.key === k);
  const isReq = (k) => fields.find((f) => f.key === k)?.required;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    for (const f of fields) {
      if (f.required && !((form[f.key] || "").toString().trim())) {
        setErr(`Le champ « ${f.label} » est requis.`); return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/booking`, {
        name: form.name || "", email: form.email || "", phone: form.phone || "", company: form.company || "",
        address: form.address || "", service_type: form.service_type || "", vehicle_brand: form.vehicle_brand || "",
        vehicle_model: form.vehicle_model || "", vehicle_plate: form.vehicle_plate || "",
        preferred_date: form.preferred_date ? new Date(form.preferred_date).toISOString() : "",
        location: form.location || "", message: form.message || "",
      });
      setDone(true);
    } catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); }
    finally { setLoading(false); }
  };

  if (!config) return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Chargement…</div>;

  if (done) return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="max-w-md text-center bg-card border border-border rounded-2xl shadow-card p-10" data-testid="booking-success">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
        <h1 className="font-display font-black text-3xl mt-4">Demande envoyée !</h1>
        <p className="text-muted-foreground mt-3">Merci. L'équipe {config.company_name} vous recontacte rapidement pour confirmer votre réservation.</p>
        <Button className="mt-6 rounded-full" onClick={() => { setDone(false); setForm({}); }} data-testid="booking-again">Nouvelle demande</Button>
      </div>
    </div>
  );

  const textField = (key) => has(key) && (
    <div className="space-y-1.5">
      <Label>{fields.find((f) => f.key === key)?.label}{isReq(key) && <span className="text-destructive"> *</span>}</Label>
      <Input value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`booking-${key}`} />
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block grain">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
            {config.has_logo && <img src={`${API}/branding/logo`} alt="" className="h-9 w-9 rounded-lg object-contain bg-white/10 p-0.5" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            {config.company_name}
          </div>
          <div>
            <div className="text-xs uppercase tracking-overline text-accent mb-4">Réservation en ligne</div>
            <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05]">{voc.bookingTitle}</h1>
            <p className="mt-6 text-primary-foreground/70 max-w-md">Remplissez le formulaire, nous nous occupons du reste avec le plus grand soin.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-lg">
          <div className="lg:hidden font-display font-black text-2xl mb-6 flex items-center gap-2">{config.company_name}</div>
          <h2 className="font-display font-bold text-3xl tracking-tight flex items-center gap-2"><CalendarClock className="h-7 w-7 text-accent" /> Demande de réservation</h2>
          <p className="text-muted-foreground mt-2 text-sm">Les champs marqués d'un * sont obligatoires.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {textField("name")}
              {textField("email")}
              {textField("phone")}
              {textField("company")}
            </div>
            {textField("address")}
            {has("service_type") && (
              <div className="space-y-1.5">
                <Label>{voc.serviceTypeLabel}{isReq("service_type") && <span className="text-destructive"> *</span>}</Label>
                <Select value={form.service_type || ""} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger data-testid="booking-service_type"><SelectValue placeholder={voc.serviceTypePlaceholder} /></SelectTrigger>
                  <SelectContent>{(config.mission_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {(has("vehicle_brand") || has("vehicle_model")) && (
              <VehiclePicker initialBrand="" initialModel="" onChange={(brand, model) => setForm((f) => ({ ...f, vehicle_brand: brand, vehicle_model: model }))} />
            )}
            {textField("vehicle_plate")}
            {has("preferred_date") && (
              <div className="space-y-2">
                <Label>Date souhaitée{isReq("preferred_date") && <span className="text-destructive"> *</span>}</Label>
                <Input type="date" value={slotDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => loadSlots(e.target.value)} data-testid="booking-date" />
                {slotDate && (slotLoading ? (
                  <p className="text-sm text-muted-foreground">Recherche des créneaux disponibles…</p>
                ) : slots.length ? (
                  <>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
                      {slots.map((sl) => (
                        <button type="button" key={sl.start} disabled={!sl.available} onClick={() => set("preferred_date", sl.start)} data-testid={`slot-${sl.label}`}
                          className={cn("py-2 rounded-lg text-sm border transition-colors",
                            form.preferred_date === sl.start ? "bg-accent text-accent-foreground border-accent"
                              : sl.available ? "border-border hover:border-accent" : "opacity-40 cursor-not-allowed line-through")}>
                          {sl.label}
                        </button>
                      ))}
                    </div>
                    {!form.preferred_date && <p className="text-xs text-muted-foreground">Sélectionnez un créneau disponible.</p>}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun créneau ce jour. Choisissez une autre date.</p>
                ))}
              </div>
            )}
            {textField("location")}
            {has("message") && (
              <div className="space-y-1.5">
                <Label>Message / instructions{isReq("message") && <span className="text-destructive"> *</span>}</Label>
                <Textarea rows={3} value={form.message || ""} onChange={(e) => set("message", e.target.value)} data-testid="booking-message" />
              </div>
            )}
            {err && <p className="text-sm text-destructive" data-testid="booking-error">{err}</p>}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="booking-submit">{loading ? "Envoi…" : "Envoyer ma demande"}</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
